import React, { createContext, useContext, ReactNode, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from 'pages/thermography/config/store';
import { login, logout, getSession } from 'pages/thermography/shared/reducers/authentication';
import { DatabaseService } from 'pages/thermography/stores/DatabaseService';
import { usePowerStationFilterStore } from 'pages/thermography/stores/PowerStationFilterStore';

interface User {
    name: string;
    email?: string;
}

interface AuthContextType {
    isAuthenticated: boolean;
    user: User | null;
    login: (username: string, password: string) => void;
    logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};


interface AuthProviderProps {
    children: ReactNode;
}

const AuthProvider = ({ children }: AuthProviderProps) => {
    const dispatch = useAppDispatch();
    const isAuthenticated = useAppSelector(state => state.authentication.isAuthenticated);
    const sessionHasBeenFetched = useAppSelector(state => state.authentication.sessionHasBeenFetched);
    const user = useAppSelector(state => state.authentication.account);
    const loading = useAppSelector(state => state.authentication.loading);
    const refreshData = usePowerStationFilterStore((state) => state.refreshData);

    // Recuperar sessão ao montar o componente
    useEffect(() => {
        // Só tenta buscar a sessão se ainda não foi buscada
        if (!sessionHasBeenFetched) {
            dispatch(getSession());
        }
    }, [dispatch, sessionHasBeenFetched]);

    // Inicializar o banco de dados quando autenticado
    useEffect(() => {
        if (isAuthenticated) {
            const dbService = DatabaseService.getInstance();
            if (!dbService.isInitialized) {
                const initializeDatabase = async () => {
                    await dbService.initialize();
                    refreshData();
                };

                initializeDatabase();
            }
        }
    }, [isAuthenticated]);

    const handleLogin = (username: string, password: string) => {
        dispatch(login(username, password));
    };

    const handleLogout = () => {
        dispatch(logout());
    };

    // Mostrar loading enquanto verifica a sessão
    if (!sessionHasBeenFetched && loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <div>Verificando autenticação...</div>
            </div>
        );
    }

    return (
        <AuthContext.Provider value={{ isAuthenticated, user, login: handleLogin, logout: handleLogout }}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthProvider;
